/**
 * ONE LISTING COUNT, ONE NAMED BASIS (W1, Muse site audit 2026-10-20,
 * docs/audits/SITE_AUDIT_MUSE_2026-10-20.md P0-1).
 *
 * The audit read the same afternoon: home hero "3,182 LISTED" beside towns in
 * "houses for sale"; /cities "1,768 detached single-family" over a map of
 * "3,182 homes of every type"; Redmond 280 on the map, 267 at a glance, 193 in
 * the dial. Most of those pairs are both true. They count different things:
 *
 *   - the map, the search index and the homes under a place map count every
 *     home type for sale (a commercial lease is never a home for sale);
 *   - Market Truth (market_metric, segment `detached`) counts detached
 *     single-family houses only, and that is the figure the market sentences,
 *     the town tiles and the months-of-supply bars publish.
 *
 * What made them read as contradictions is that neither said which it was.
 * Every public active-listing count on the home page, /homes-for-sale, the
 * city pages and /cities is published through a selector here, and each
 * selector returns the count WITH its basis: a fixed plain-words label the
 * page prints beside the number. Two counts on one page that differ now say
 * why in the words next to them. A count that never passed through here has
 * no basis to print, and lib/listing-counts.surfaces.test.tsx fails the page.
 *
 * Round 3 (audit v2 2.1 / 2.2, 2026-10-21) added the populations a search
 * card and a capped map read: the $/sq ft band counts only the homes that
 * report a living area (livingAreaListingCount); a map drawing fewer pins
 * than the list total says so as "134 of 269 on map" (onMapOfTotalText); and
 * a search total never moves with the sort (searchPageTotalCount).
 *
 * Client-safe and dependency-light: V3Atlas, PlaceSubdivisionHomes,
 * MapSearchView, AllFiltersSheet and SearchResults (client components) call
 * it, as do the server pages.
 */
import { formatCount } from '@/lib/format/count'
import { publicCountState } from '@/lib/listing-status-public'
import { publicSegmentNoun } from '@/lib/data/market-truth/public-segment-view'

export type ListingCountBasisKey =
  /** Every home type for sale: house, condo, townhouse, manufactured, land, multi-family, commercial sale. */
  | 'all-types'
  /** Market Truth's detached segment: single-family houses only. */
  | 'detached'
  /** The reader narrowed the types (a map toggle, a search filter). */
  | 'chosen-types'
  /** One buyer group under a place map ("Houses", "Lots"). */
  | 'group'
  /** One Market Truth property-type segment ("condos", "lots"). */
  | 'segment'
  /** The homes in view that report a living area: the $/sq ft band's population. */
  | 'living-area'

export type ListingCountBasis = {
  key: ListingCountBasisKey
  /** The plain words the page prints beside the number. */
  label: string
}

export type ListingCount = {
  count: number
  basis: ListingCountBasis
}

/** The two fixed bases a public count may claim without a reader's filter. */
export const ALL_TYPES_BASIS: ListingCountBasis = Object.freeze({ key: 'all-types', label: 'all home types' })
export const DETACHED_BASIS: ListingCountBasis = Object.freeze({ key: 'detached', label: 'detached single-family' })

/** The basis of a search count the reader narrowed by property type. */
export const CHOSEN_TYPES_BASIS: ListingCountBasis = Object.freeze({
  key: 'chosen-types',
  label: 'the home types you chose',
})

/**
 * The $/sq ft band's population (components/search/ppsf-band.ts): only the
 * homes in view that report a living area have a price per square foot, so
 * the band counts fewer homes than the claim over the same rail (audit v2
 * 2.1: "first 489 on the map" over "of 431 homes on this map"). The words are
 * the ones the band's screen-reader label already says (bandLabel).
 */
export const LIVING_AREA_BASIS: ListingCountBasis = Object.freeze({
  key: 'living-area',
  label: 'that report a living area',
})

/**
 * What a count reads when no source published one. Unknown is not zero, and
 * it is not an error either: the words say the figure was not published, so
 * the row reads as a plain fact rather than a broken cell (Muse audit
 * 2026-10-20: "No live count" on /cities read as broken).
 */
export const UNPUBLISHED_COUNT_LABEL = 'Count not published'

function wholeCount(n: number | null | undefined): number | null {
  if (n == null || !Number.isFinite(n) || n < 0) return null
  return Math.round(n)
}

function joinAnd(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? ''
  if (words.length === 2) return `${words[0]} and ${words[1]}`
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
}

/* -------------------------------------------------------------------------- */
/* Selectors, one per source                                                   */
/* -------------------------------------------------------------------------- */

export type MapTypeToggle = { key: string; label: string }

/**
 * The map (V3Atlas, buildPlaceAtlas): Active listings with a coordinate, every
 * type but a commercial lease. Also the /cities map claim and the home pulse
 * band's map figure, which read the same population. (The home hero's
 * "listed" figure is the search index's, the number /homes-for-sale prints:
 * W23, searchListingCount.)
 *
 * With `filter`, the basis follows the map's own toggles: every type on and
 * no price ceiling is "all home types"; otherwise the types still on, by
 * name, and the ceiling ("house and condo only, under $600K").
 */
export function mapListingCount(
  counts: { forSale: number },
  filter?: {
    typesOn: readonly MapTypeToggle[]
    types: readonly MapTypeToggle[]
    /** Preformatted ceiling ("$600K"), or null at any price. */
    maxPriceLabel?: string | null
  },
): ListingCount {
  const count = wholeCount(counts.forSale) ?? 0
  if (!filter) return { count, basis: ALL_TYPES_BASIS }
  const allOn = filter.typesOn.length >= filter.types.length
  const ceiling = filter.maxPriceLabel?.trim() ? `under ${filter.maxPriceLabel.trim()}` : null
  if (allOn && !ceiling) return { count, basis: ALL_TYPES_BASIS }
  const types = allOn
    ? ALL_TYPES_BASIS.label
    : `${joinAnd(filter.typesOn.map((t) => t.label.toLowerCase()))} only`
  return {
    count,
    basis: { key: 'chosen-types', label: ceiling ? `${types}, ${ceiling}` : types },
  }
}

/**
 * The search index (searchListingsAll / searchListingsAllCount): every type
 * but a commercial lease, unless the reader chose a property type. The home
 * hero's "N listed" reads this too (W23), from the same sort-free count
 * /homes-for-sale publishes, so the two pages print one number.
 */
export function searchListingCount(total: number, opts: { typeFiltered?: boolean } = {}): ListingCount {
  return {
    count: wholeCount(total) ?? 0,
    basis: opts.typeFiltered ? CHOSEN_TYPES_BASIS : ALL_TYPES_BASIS,
  }
}

/**
 * The total one page of a search index read publishes (searchListingsAll).
 * dedupeListingTilesByStreet folds the same-street copies among the rows
 * READ, so taking what it removed off the exact count is sound only when the
 * read held every row. On a capped page, which copies share the page depends
 * on the sort and the offset, and the total moved with the sort (audit v2
 * 2.1: the under-$500K total shifted on a sort change). A read short of the
 * whole set keeps the exact count, the figure searchListingsAllCount
 * publishes for the same filters, so no sort or page can change it.
 */
export function searchPageTotalCount(input: {
  /** The exact count for the filters, or null when it did not return. */
  count: number | null
  offset: number
  /** Rows the read returned, before the street dedupe. */
  rawRead: number
  /** Rows the street dedupe removed from them. */
  removed: number
}): number {
  const count = wholeCount(input.count) ?? wholeCount(input.rawRead) ?? 0
  const wholeSet = input.offset === 0 && input.rawRead >= count
  return Math.max(0, wholeSet ? count - Math.max(0, input.removed) : count)
}

/**
 * Whether a search's URL params narrow it by property type, the
 * `typeFiltered` searchListingCount takes: a property type other than "all",
 * or any property sub type.
 */
export function searchTypeFiltered(params: {
  propertyType?: string | null
  propertySubTypes?: string | null
}): boolean {
  const type = params.propertyType?.trim()
  return Boolean((type && type !== 'all') || params.propertySubTypes?.trim())
}

/**
 * Market Truth's active count (market_metric segment `detached`,
 * leftoverHudKpis().active, the city snapshot overlay). Null when the source
 * published nothing: unknown is not zero.
 */
export function detachedListingCount(active: number | null | undefined): ListingCount | null {
  const count = wholeCount(active)
  return count == null ? null : { count, basis: DETACHED_BASIS }
}

/**
 * One Market Truth property-type segment (market_metric active_count for
 * `condo`, `land`, ...), as "Other {city} property types" on a city search
 * page lists them. The type's own noun is the basis ("condos", "lots"), so
 * the label it prints never needs a second qualifier. Null when the source
 * published nothing.
 */
export function segmentListingCount(segment: string, active: number | null | undefined): ListingCount | null {
  const count = wholeCount(active)
  return count == null ? null : { count, basis: { key: 'segment', label: publicSegmentNoun(segment, count) } }
}

/**
 * The $/sq ft band under each search card (buildPpsfBand's `n`): the homes in
 * view that report a living area, never the rail's whole count.
 */
export function livingAreaListingCount(n: number): ListingCount {
  return { count: wholeCount(n) ?? 0, basis: LIVING_AREA_BASIS }
}

export type PlaceStockRow = { standardStatus?: string | null }

/**
 * The homes under a place map (loadPlaceStockTiles): every type for sale,
 * with Active Under Contract held apart (publicCountState, SITE-193). A
 * `group` names one buyer group's dial instead of every type; `mapBasis` is
 * the basis the map's key names when the reader filtered it (the rows are
 * then the filtered rows, lib/atlas/atlas-filter.ts).
 */
export function placeStockListingCount(
  rows: readonly PlaceStockRow[],
  group?: { heading: string } | null,
  mapBasis?: ListingCountBasis | null,
): ListingCount & { underContract: number } {
  let forSale = 0
  let underContract = 0
  for (const row of rows) {
    // A row read with no status is what the public status policy treats as
    // on the market (isPubliclyDisplayableStatus), so it counts for sale.
    if (publicCountState(row.standardStatus) === 'under-contract') underContract += 1
    else forSale += 1
  }
  const basis: ListingCountBasis = group?.heading.trim()
    ? { key: 'group', label: group.heading.trim().toLowerCase() }
    : (mapBasis ?? ALL_TYPES_BASIS)
  return { count: forSale, underContract, basis }
}

/* -------------------------------------------------------------------------- */
/* List vs map                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * W23 (site audit 2026-10-21, sections 2.1 and 2.2): a map can show fewer
 * homes than the list counts for the same filters. The map reads a frame (a
 * place's bounding box, the viewport) and only homes with a recorded
 * coordinate inside it; the list reads the place by name. Prineville under
 * $500K read 269 in the list and 134 on the map with nothing on screen to say
 * why. Both are true; the screen now says "134 of 269 on the map".
 *
 * Null when there is no gap to explain: the list figure is unknown, or it is
 * not larger than the map's (a list figure smaller than the map's is two reads
 * out of step, not a share, and is never printed as "N of M").
 */
export type MapShare = { onMap: number; of: number }

export function mapShare(onMap: number, listTotal: number | null | undefined): MapShare | null {
  const shown = wholeCount(onMap)
  const of = wholeCount(listTotal)
  if (shown == null || of == null || of <= shown) return null
  return { onMap: shown, of }
}

/** "134 of 269 on the map". `where` swaps the tail ("listed" before the map mounts). */
export function mapShareText(share: MapShare, where = 'on the map'): string {
  return `${formatCount(share.onMap)} of ${formatCount(share.of)} ${where}`
}

/* -------------------------------------------------------------------------- */
/* Printing                                                                    */
/* -------------------------------------------------------------------------- */

/** "{text}, {basis}": the basis rides right after the phrase it qualifies. */
export function withCountBasis(text: string, basis: ListingCountBasis): string {
  return `${text}, ${basis.label}`
}

/** "280 for sale, all home types" / "280 for sale on the map, all home types". */
export function forSaleText(c: ListingCount, scope?: string | null): string {
  const where = scope?.trim() ? ` ${scope.trim()}` : ''
  return withCountBasis(`${formatCount(c.count)} for sale${where}`, c.basis)
}

/**
 * The search header (W1 finish, audit v2 2.1): the homes for sale, with their
 * basis, then the rest of the list by name. A search's list holds Active Under
 * Contract homes (and Pending on the with-pending scope) beside the homes for
 * sale; a public count calls those under contract (SITE-193), so the line
 * leads with the for-sale figure every other surface prints and the two parts
 * add up to the list: "3,166 homes for sale, all home types, plus 24 under
 * contract". `listed` is the list's own total.
 */
export function forSaleOfListedText(forSale: ListingCount, listed: number): string {
  const head = withCountBasis(
    `${formatCount(forSale.count)} ${forSale.count === 1 ? 'home' : 'homes'} for sale`,
    forSale.basis,
  )
  const rest = Math.max(0, (wholeCount(listed) ?? 0) - forSale.count)
  return rest > 0 ? `${head}, plus ${formatCount(rest)} under contract` : head
}

/**
 * "134 of 269 on map" (audit v2 2.2): the pins the map draws against the list
 * total, when the map draws fewer (the display cap, a home with no
 * coordinate). Null when every home in the total is on the map, so the page
 * prints its one count as before. A total that is only a floor reads "N+".
 */
export function onMapOfTotalText(
  onMap: number,
  total: number | ListingCount,
  opts: { totalIsFloor?: boolean } = {},
): string | null {
  const pins = wholeCount(onMap) ?? 0
  const all = wholeCount(typeof total === 'number' ? total : total.count) ?? 0
  if (pins >= all && !opts.totalIsFloor) return null
  return `${formatCount(pins)} of ${formatCount(all)}${opts.totalIsFloor ? '+' : ''} on map`
}

/**
 * The basis line under a map's reach list (V3Atlas chips): each chip counts
 * the marks inside a place's shape, for sale and under contract, which is
 * neither a place's for-sale count nor its detached count, so it says so.
 */
export function reachChipBasisText(basis: ListingCountBasis): string {
  return `Each count is the homes for sale and under contract inside that place on this map, ${basis.label}.`
}

/** A name that opens with the basis: basisName(DETACHED_BASIS, 'houses') is "Detached single-family houses". */
export function basisName(basis: ListingCountBasis, noun: string): string {
  const words = `${basis.label} ${noun}`.trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** "detached single-family homes" / "detached single-family home", for a sentence that names its own noun. */
export function detachedHomesNoun(n: number): string {
  return `${DETACHED_BASIS.label} ${n === 1 ? 'home' : 'homes'}`
}

/** "detached single-family houses" / "... house". */
export function detachedHousesNoun(n: number): string {
  return `${DETACHED_BASIS.label} ${n === 1 ? 'house' : 'houses'}`
}
